// The CHAT INK fold of SETTINGS · ACCOUNT (monetization.md §3.3, R3290).
//
// The chat ink colours the name of the account in the clan chat, for
// every member of the clan. The fold shows the tier tag of the
// `clan_identity` key, the list of the inks with NONE first, and a sample
// of the name in the picked ink. A press saves at once: a code through
// PUT, which needs the key, and NONE through DELETE, which needs none
// (R1241). Without the key the inks show locked and NONE stays open.

import { CHAT_INKS } from '../../../gen/presentation';
import { clanLook as S } from '../../../gen/strings';
import { KEY_CLAN_IDENTITY, getChatInk, putChatInk, resetChatInk } from '../../../net/api';
import { valueChoice, type Choice } from '../../choice/choice';
import { asChatInk, chatInkNameKey, inkStyle } from '../../clanlook';
import { esc } from '../../esc';
import { wordOf } from '../../../locale/wordof';
import { hasKey, TTL_ON_OPEN, type Cell } from '../hubstate';
import { lede, tierTag, type PanelContext } from '../panel';
import { errNote, runHeld, type HeldCall } from './optcards';

/** The cache key of `GET /api/chat-ink`. */
export const CHAT_INK_CELL = 'chat-ink';

/** The act of a press on one option. */
export const CHAT_INK_ACT = 'chat-ink';

/** The options of the list: NONE, then each ink of the data layer.
 *  `held` says whether the account holds the key; NONE never locks. */
export function chatInkChoices(held: boolean): Choice[] {
  return [
    { id: '', label: S.ink_none },
    ...CHAT_INKS.map((r) => ({
      id: r.code, label: wordOf(S, chatInkNameKey(r.code), r.code.toUpperCase()), ink: r.ink, locked: !held,
    })),
  ];
}

/** What a press on `pick` sends, given the stored ink: nothing, a PUT of
 *  the code, or the DELETE of NONE. */
export function chatInkSave(stored: string, pick: string): 'none' | 'put' | 'delete' {
  const p = asChatInk(pick);
  if (p === asChatInk(stored) || (p === '' && pick !== '')) return 'none';
  return p === '' ? 'delete' : 'put';
}

/** The card the ACCOUNT page mounts. */
export interface ChatInkCard {
  html(): string;
  act(action: string, el: HTMLElement): boolean;
}

export function createChatInkCard(ctx: PanelContext): ChatInkCard {
  const call: HeldCall = { busy: false, err: '' };
  const cell = (): Cell<string> => ctx.data.cell<string>(CHAT_INK_CELL, TTL_ON_OPEN, getChatInk);

  return {
    html(): string {
      const me = ctx.me();
      const held = hasKey(me, KEY_CLAN_IDENTITY);
      const stored = asChatInk(cell().value);
      // The sample is the name of the account in the picked ink: the text
      // is the name as the clan reads it, and only its colour changes.
      const sample = `<p class="inksample"><span class="who"${inkStyle(stored)}>${esc(me.account.name)}</span></p>`;
      return tierTag('supporter', held, me)
        + lede(S.ink_lede)
        + valueChoice({
          list: chatInkChoices(held), chosen: stored, act: CHAT_INK_ACT, label: S.ink_section,
          floor: 'list',
        })
        + sample
        + lede(call.busy ? S.ink_saving : S.ink_rule)
        + errNote(call.err);
    },

    act(action: string, el: HTMLElement): boolean {
      if (action !== CHAT_INK_ACT) return false;
      if (call.busy) return true;
      const pick = el.dataset.id ?? '';
      const what = chatInkSave(cell().value ?? '', pick);
      if (what === 'none') return true;
      runHeld(ctx, call, 'sfx-menu-press',
        () => (what === 'put' ? putChatInk(pick) : resetChatInk()),
        // The next draw reads the stored ink again.
        () => { ctx.data.drop(CHAT_INK_CELL); });
      return true;
    },
  };
}
